import moment from 'moment-timezone';
import { getStartOfWeek, subtractTime, getStartOf } from 'util/dates';
import { fetchStripeAccount } from 'ducks/stripeConnectAccount.duck';
import { fetchCurrentUser } from '../../../ducks/user.duck';
import { storableError } from '../../../util/errors';
import { getUpcomingClasses } from '../api';
import { types as sdkTypes } from '../../../util/sdkLoader';

const { UUID } = sdkTypes;

// ================ Action types ================ //

const QUERY_SALES_REQUEST = 'app/DashboardPage/QUERY_SALES_REQUEST';
const QUERY_SALES_SUCCESS = 'app/DashboardPage/QUERY_SALES_SUCCESS';
const QUERY_SALES_ERROR = 'app/DashboardPage/QUERY_SALES_ERROR';

const QUERY_LASTWEEK_SALES_REQUEST = 'app/DashboardPage/QUERY_LASTWEEK_SALES_REQUEST';
const QUERY_LASTWEEK_SALES_SUCCESS = 'app/DashboardPage/QUERY_LASTWEEK_SALES_SUCCESS';
const QUERY_LASTWEEK_SALES_ERROR = 'app/DashboardPage/QUERY_LASTWEEK_SALES_ERROR';

const QUERY_UPCOMING_CLASSES_REQUEST = 'app/DashboardPage/QUERY_UPCOMING_CLASSES_REQUEST';
const QUERY_UPCOMING_CLASSES_SUCCESS = 'app/DashboardPage/QUERY_UPCOMING_CLASSES_SUCCESS';
const QUERY_UPCOMING_CLASSES_ERROR = 'app/DashboardPage/QUERY_UPCOMING_CLASSES_ERROR';

// ================ Reducer ================ //
const REVENUE_STATES = ['state/accepted', 'state/delivered', 'state/reviewed'];
const TIMEZONE = 'Europe/London';

const initialState = {
  querySalesInProgress: false,
  querySalesError: null,
  queryLastWeekSalesInProgress: false,
  queryLastWeekSalesError: null,
  totalBookings: 0,
  lastWeekBookings: 0,
  totalRevenue: 0,
  lastWeekRevenue: 0,
  upcomingClasses: [],
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case QUERY_SALES_REQUEST:
      return {
        ...state,
        querySalesInProgress: true,
        querySalesError: null,
      };
    case QUERY_SALES_SUCCESS:
      return {
        ...state,
        querySalesInProgress: false,
        totalBookings: payload.totalBookings,
        totalRevenue: payload.totalRevenue,
      };
    case QUERY_SALES_ERROR:
      return {
        ...state,
        querySalesInProgress: false,
        querySalesError: payload,
      };
    case QUERY_LASTWEEK_SALES_REQUEST:
      return {
        ...state,
        queryLastWeekSalesInProgress: true,
        queryLastWeekSalesError: null,
      };
    case QUERY_LASTWEEK_SALES_SUCCESS:
      return {
        ...state,
        queryLastWeekSalesInProgress: false,
        lastWeekBookings: payload.lastWeekBookings,
        lastWeekRevenue: payload.lastWeekRevenue,
      };
    case QUERY_LASTWEEK_SALES_ERROR:
      return {
        ...state,
        queryLastWeekSalesInProgress: false,
        queryLastWeekSalesError: payload,
      };
    case QUERY_UPCOMING_CLASSES_REQUEST:
      return {
        ...state,
        queryUpcomingClassesInProgress: true,
        queryUpcomingClassesError: null,
      };
    case QUERY_UPCOMING_CLASSES_SUCCESS:
      return {
        ...state,
        queryUpcomingClassesInProgress: false,
        upcomingClasses: payload.upcomingClasses,
      };
    case QUERY_UPCOMING_CLASSES_ERROR:
      return {
        ...state,
        queryUpcomingClassesInProgress: false,
        queryUpcomingClassesError: payload,
      };
    default:
      return state;
  }
}

// ================ Helper functions ================ //
const getLast7DaysRange = () => {
  const end = moment.tz(TIMEZONE).toDate();
  const start = getStartOf(subtractTime(end, 7, 'days', TIMEZONE), 'day', TIMEZONE);

  return {
    start: start.toISOString(),
    end: end.toISOString(),
  };
};
const getLastWeekRange = () => {
  const today = moment.tz(TIMEZONE).toDate();
  const thisWeekStart = getStartOfWeek(today, TIMEZONE, 1);
  const lastWeekStart = subtractTime(thisWeekStart, 7, 'days', TIMEZONE);

  return {
    start: lastWeekStart.toISOString(),
    end: thisWeekStart.toISOString(),
  };
};

const calculateTotalPayout = (transactions) => {
  return transactions.reduce((total, transaction) => {
    const payoutAmount = transaction.attributes?.payoutTotal?.amount || 0;
    return total + payoutAmount;
  }, 0);
};

// ================ Action creators ================ //

export const querySalesRequest = () => ({ type: QUERY_SALES_REQUEST });
export const querySalesSuccess = (totalBookings, totalRevenue) => ({
  type: QUERY_SALES_SUCCESS,
  payload: { totalBookings, totalRevenue },
});
export const querySalesError = (error) => ({
  type: QUERY_SALES_ERROR,
  payload: error,
  error: true,
});
export const queryLastWeekSalesRequest = () => ({ type: QUERY_LASTWEEK_SALES_REQUEST });
export const queryLastWeekSalesSuccess = (lastWeekBookings, lastWeekRevenue) => ({
  type: QUERY_LASTWEEK_SALES_SUCCESS,
  payload: { lastWeekBookings, lastWeekRevenue },
});
export const queryLastWeekSalesError = (error) => ({
  type: QUERY_LASTWEEK_SALES_ERROR,
  payload: error,
  error: true,
});

export const queryUpcomingClassesRequest = () => ({ type: QUERY_UPCOMING_CLASSES_REQUEST });
export const queryUpcomingClassesSuccess = (upcomingClasses) => ({
  type: QUERY_UPCOMING_CLASSES_SUCCESS,
  payload: { upcomingClasses },
});
export const queryUpcomingClassesError = (error) => ({
  type: QUERY_UPCOMING_CLASSES_ERROR,
  payload: error,
  error: true,
});

// ================ Thunks ================ //

export const querySales = (props) => async (dispatch, getState, sdk) => {
  const { startDate, endDate } = props || {};

  const queryParams = {
    only: 'sale',
    prot_ticketOption: ['blockBooking', 'singleEvent', 'payAsYouGo', 'freeTrial'],
    include: ['booking', 'listing'],
    ...(startDate && { createdAtStart: startDate }),
    ...(endDate && { createdAtEnd: endDate }),
  };

  return sdk.transactions.query(queryParams);
};

export const queryLast7DaysSales = () => async (dispatch) => {
  const { start } = getLast7DaysRange();
  dispatch(querySalesRequest());
  try {
    const response = await dispatch(querySales({ startDate: start }));
    const transactions = response.data.data;
    const filteredTransactions = transactions.filter((transaction) =>
      REVENUE_STATES.includes(transaction.attributes.state)
    );
    const totalRevenue = calculateTotalPayout(filteredTransactions);

    const totalBookings = transactions.length;
    dispatch(querySalesSuccess(totalBookings, totalRevenue));
    return response;
  } catch (e) {
    dispatch(querySalesError(storableError(e)));
  }
};

export const queryLastWeekSales = () => (dispatch) => {
  const { start, end } = getLastWeekRange();
  dispatch(queryLastWeekSalesRequest());
  return dispatch(querySales({ startDate: start, endDate: end }))
    .then((response) => {
      const transactions = response.data.data;
      const filteredTransactions = transactions.filter((transaction) =>
        REVENUE_STATES.includes(transaction.attributes.state)
      );
      const lastWeekRevenue = calculateTotalPayout(filteredTransactions);
      const lastWeekBookings = transactions.length;
      dispatch(queryLastWeekSalesSuccess(lastWeekBookings, lastWeekRevenue));
      return response;
    })
    .catch((e) => {
      dispatch(queryLastWeekSalesError(storableError(e)));
    });
};

export const queryUpcomingClasses = () => async (dispatch) => {
  dispatch(queryUpcomingClassesRequest());
  try {
    const response = await getUpcomingClasses();
    const { data } = response;
    dispatch(queryUpcomingClassesSuccess(data));
    return response;
  } catch (e) {
    dispatch(queryUpcomingClassesError(storableError(e)));
  }
};

export const loadData = () => async (dispatch, getState) => {
  await dispatch(fetchCurrentUser());
  const { currentUser } = getState().user;
  const promises = [
    dispatch(queryLast7DaysSales()),
    dispatch(queryLastWeekSales()),
    dispatch(queryUpcomingClasses()),
  ];

  if (currentUser && currentUser.stripeAccount) {
    promises.push(dispatch(fetchStripeAccount()));
  }
  return Promise.all(promises);
};

// ================ Selectors ================ //
export const upcomingClassesSelector = (state) => state.DashboardPage.upcomingClasses;
export const totalBookingsSelector = (state) => state.DashboardPage.totalBookings;
export const querySalesInProgressSelector = (state) => state.DashboardPage.querySalesInProgress;
export const querySalesErrorSelector = (state) => state.DashboardPage.querySalesError;
export const queryLastWeekSalesInProgressSelector = (state) =>
  state.DashboardPage.queryLastWeekSalesInProgress;
export const queryLastWeekSalesErrorSelector = (state) =>
  state.DashboardPage.queryLastWeekSalesError;
export const lastWeekBookingsSelector = (state) => state.DashboardPage.lastWeekBookings;
export const totalRevenueSelector = (state) => state.DashboardPage.totalRevenue;
export const lastWeekRevenueSelector = (state) => state.DashboardPage.lastWeekRevenue;
